import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { SubentityState } from 'app/projects/subentity/src/lib/models/subentity.state';
import { map } from 'rxjs/operators';
import {
    AddAllScheduledStreamContents,
    CreateScheduledStreamContentSuccess,
    DeleteScheduledStreamContent,
    DeselectSelectedScheduledStreamContents,
    FetchScheduledStreamContents,
    FetchScheduledStreamContentsFailure,
    FetchScheduledStreamContentsSuccess,
    SearchScheduledStreamContents,
    SelectFilteredScheduledStreamContents,
    SetScheduledStreamContents,
    ToggleSelectedScheduledStreamContent,
} from '../actions';
import { AddScheduledStreamContents } from '../actions/add-entities.action';
import { ScheduledStreamContentApiService } from '../services/scheduled-stream-content.api-service';
import { MaxBrainScheduledStreamContent } from './scheduled-stream-content.model';
import { ScheduledStreamContentState } from './scheduled-stream-content.state';
import { ScheduledStreamContentsStateModel } from './scheduled-stream-contents.state-model';
import { UtilsState } from 'app/projects/subentity/src/lib/models/utils.state';

@State<ScheduledStreamContentsStateModel>({
    name: 'scheduledStreamContents',
    defaults: new ScheduledStreamContentsStateModel(),
})
@Injectable()
export class ScheduledStreamContentsState extends SubentityState<MaxBrainScheduledStreamContent> {
    private _pendingFetchPayload: FetchScheduledStreamContents['payload'] = null;

    @Selector([ScheduledStreamContentState.getEntities])
    static getFilteredEntities(state: ScheduledStreamContentsStateModel, scheduledStreamContents: MaxBrainScheduledStreamContent[]): MaxBrainScheduledStreamContent[] {
        if (!scheduledStreamContents) {
            return null;
        }

        return scheduledStreamContents
            .filter((item) => state.filtered.indexOf(item.id) !== -1)
            .map((item) => {
                return [state.list.indexOf(item.id), item];
            })
            .sort((a: [number, MaxBrainScheduledStreamContent], b: [number, MaxBrainScheduledStreamContent]) => {
                return a[0] > b[0] ? 1 : b[0] > a[0] ? -1 : 0;
            })
            .map((x: [number, MaxBrainScheduledStreamContent]) => x[1]);
    }

    @Selector()
    static getSelectedEntityIds(state: ScheduledStreamContentsStateModel): string[] {
        return state.selected;
    }

    @Selector()
    static getSearchTerm(state: ScheduledStreamContentsStateModel): string {
        return state.searchTerm;
    }

    @Selector()
    static getTotalCount(state: ScheduledStreamContentsStateModel): number {
        return state.totalCount;
    }

    @Selector()
    static isFetchingList(state: ScheduledStreamContentsStateModel): boolean {
        return state.fetchingList;
    }

    @Selector()
    static getPageNumber(state: ScheduledStreamContentsStateModel): number {
        return state.pageNumber || 1;
    }

    constructor(private store: Store, private _scheduledStreamContentApiService: ScheduledStreamContentApiService) {
        super(null, 0);

        this._propstoSearch = [...MaxBrainScheduledStreamContent.props_to_search];
    }

    @Action(AddScheduledStreamContents)
    addEntities(ctx: StateContext<ScheduledStreamContentsStateModel>, action: AddScheduledStreamContents): void {
        const scheduledStreamContents = this.store.selectSnapshot(ScheduledStreamContentState.getEntities) || [];

        this._patchSubentities(ctx, { payload: { subentityIds: action.payload.map((entity) => entity.id) } }, scheduledStreamContents);
    }

    @Action(CreateScheduledStreamContentSuccess)
    createEntity(ctx: StateContext<ScheduledStreamContentsStateModel>, action: CreateScheduledStreamContentSuccess): void {
        const scheduledStreamContents = this.store.selectSnapshot(ScheduledStreamContentState.getEntities) || [];

        this._patchSubentities(ctx, { payload: { subentityIds: [action.payload.id] } }, scheduledStreamContents);
    }

    @Action(DeleteScheduledStreamContent)
    deleteEntity({ getState, patchState }: StateContext<ScheduledStreamContentsStateModel>, { payload }: DeleteScheduledStreamContent): void {
        const state = getState();

        patchState({
            list: [...state.list.filter((subentityId) => subentityId !== payload)],
            selected: [...state.selected.filter((subentityId) => subentityId !== payload)],
            filtered: [...state.filtered.filter((subentityId) => subentityId !== payload)],
        });
    }

    @Action(SetScheduledStreamContents)
    setEntities(ctx: StateContext<ScheduledStreamContentsStateModel>, action: SetScheduledStreamContents): void {
        this._setSubentities(ctx, action, null);
    }

    @Action(ToggleSelectedScheduledStreamContent)
    toggleSelect(ctx: StateContext<ScheduledStreamContentsStateModel>, action: ToggleSelectedScheduledStreamContent): void {
        this._toggleSelectedSubentity(ctx, action);
    }

    @Action(SelectFilteredScheduledStreamContents)
    selectAll(ctx: StateContext<ScheduledStreamContentsStateModel>): void {
        this._selectFilteredSubentities(ctx);
    }

    @Action(DeselectSelectedScheduledStreamContents)
    deselectAll(ctx: StateContext<ScheduledStreamContentsStateModel>): void {
        this._deselectSelectedSubentities(ctx);
    }

    @Action(SearchScheduledStreamContents)
    search(ctx: StateContext<ScheduledStreamContentsStateModel>, action: SearchScheduledStreamContents): void {
        const scheduledStreamContents = this.store.selectSnapshot(ScheduledStreamContentState.getEntities);

        if (!action.payload) {
            UtilsState.search(ctx, '', ScheduledStreamContentsStateModel.identifier);
        } else {
            ctx.patchState({ searchTerm: action.payload });
        }

        this._searchSubentities(ctx, action, scheduledStreamContents);
    }

    @Action(AddAllScheduledStreamContents)
    addAll(ctx: StateContext<ScheduledStreamContentsStateModel>, action: AddAllScheduledStreamContents): void {
        const ssc = this.store.selectSnapshot(ScheduledStreamContentState.getEntitiesAsMap);
        const allSsc = (ctx.getState().list || []).map((id) => ssc.get(id));

        const pageNumber = ctx.getState().pageNumber;

        const data = pageNumber === 1 ? action.payload : [...new Set([...allSsc, ...action.payload])];

        this._setSubentities(ctx, { payload: data }, AddScheduledStreamContents);
    }

    @Action(FetchScheduledStreamContents)
    fetchAll(ctx: StateContext<ScheduledStreamContentsStateModel>, { payload }: FetchScheduledStreamContents): void {
        if (ctx.getState().fetchingList) {
            this._pendingFetchPayload = payload;
            return;
        }

        ctx.patchState({
            pageSize: payload.pageSize,
            pageNumber: payload.pageNumber,
        });

        this._fetchSubentities(
            ctx,
            { payload: 'all' },
            SetScheduledStreamContents,
            FetchScheduledStreamContentsSuccess,
            FetchScheduledStreamContentsFailure,
            this._scheduledStreamContentApiService
                .getAllByAudienceAndStreamId(
                    payload.audienceId,
                    payload.featuredStreamContentId,
                    payload.pageSize,
                    payload.pageNumber,
                    payload.sortBy,
                    payload.sortOrder,
                    payload.search,
                    payload.selectedPostingTypeId
                )
                .pipe(
                    map((data) => {
                        ctx.patchState({
                            totalCount: data.pagination.number_of_results,
                        });

                        return data.data.map((query) => new MaxBrainScheduledStreamContent(query));
                    })
                )
        );
    }

    @Action(FetchScheduledStreamContentsSuccess)
    fetchEntitiesSuccess(ctx: StateContext<ScheduledStreamContentsStateModel>): void {
        if (ctx.getState().totalCount > ctx.getState().pageNumber) {
            this._scheduledStreamContentApiService.updateIsThereMorePages(true);
        } else {
            this._scheduledStreamContentApiService.updateIsThereMorePages(false);
        }
        this._fetchSubentitiesSuccess(ctx);
        this._dispatchPendingFetch(ctx);
    }

    @Action(FetchScheduledStreamContentsFailure)
    fetchEntitiesFailure(ctx: StateContext<ScheduledStreamContentsStateModel>): void {
        this._fetchSubentitiesFailure(ctx);
        this._dispatchPendingFetch(ctx);
    }

    private _dispatchPendingFetch(ctx: StateContext<ScheduledStreamContentsStateModel>): void {
        if (!this._pendingFetchPayload) {
            return;
        }

        const pendingPayload = this._pendingFetchPayload;
        this._pendingFetchPayload = null;
        ctx.dispatch(new FetchScheduledStreamContents(pendingPayload));
    }
}
